/**
 * Loro docs in the browser (design spec §9.1, §10): one WebSocket per device multiplexing every doc,
 * IndexedDB persistence, offline queue, reconnect with backoff, schema-version gate (I-4).
 *
 * Verified against loro-crdt 1.16.3: `doc.subscribeLocalUpdates(bytes => …)` fires per commit with the
 * update bytes; `doc.export({mode:"update", from: vv})`; `VersionVector.decode/encode`;
 * `doc.oplogVersion()`; `doc.import(bytes)` returns an ImportStatus.
 */
import { LoroDoc, VersionVector, decodeImportBlobMeta } from "loro-crdt";
import { decode, encode } from "@msgpack/msgpack";
import { appendPending, clearPending, loadPending, loadSnapshot, saveSnapshot, wipeDoc } from "./persistence";
import { SCHEMA_VERSION } from "../core/schema";
import { undoManagerFor } from "./undo";

export type SyncStatus = "offline" | "connecting" | "syncing" | "synced" | "error" | "forbidden";

export interface DocHandle {
  docId: string;
  doc: LoroDoc;
  /** the server's schema is newer than this build: read-only until reload (I-4) */
  readOnly: boolean;
  /** first server answer (or first failure) has arrived */
  settled: boolean;
  /** pending local updates not yet acknowledged */
  pendingCount: number;
  /** replacement generation of the doc on the server (see `reset`) */
  epoch: number;
}

type Listener = () => void;

interface Entry {
  handle: DocHandle;
  pendingIds: number[];
  /** IndexedDB rows of updates sent on this connection and not yet acknowledged, oldest first */
  inFlight: Promise<number | null>[];
  listeners: Set<Listener>;
  /** vv when the last update was sent; the next local commit exports from here */
  restored: Promise<void>;
}

const SNAPSHOT_EVERY_MS = 5000;

/** An exported update with no changes is still a ~22-byte envelope; this tells the two apart. */
function hasOps(update: Uint8Array): boolean {
  try {
    return decodeImportBlobMeta(update, false).changeNum > 0;
  } catch {
    return update.length > 0;
  }
}

class DocManager {
  private ws: WebSocket | null = null;
  private entries = new Map<string, Entry>();
  private status: SyncStatus = "offline";
  private statusListeners = new Set<(s: SyncStatus) => void>();
  private backoffMs = 1000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private snapshotTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private started = false;
  private outage = false;
  private urlOverride: string | null = null;

  // --- status -------------------------------------------------------------------------------

  getStatus(): SyncStatus {
    return this.status;
  }

  subscribeStatus(cb: (s: SyncStatus) => void): () => void {
    this.statusListeners.add(cb);
    return () => this.statusListeners.delete(cb);
  }

  /** Whether the connection was lost and everything is not synced again yet. Unlike `status`, this stays
   *  on through the reconnect attempts in between, so what shows it does not flicker (spec §9.19). */
  isOutage(): boolean {
    return this.outage;
  }

  private setStatus(s: SyncStatus): void {
    if (this.status === s) return;
    this.status = s;
    if (s === "offline" || s === "error") this.outage = true;
    else if (s === "synced") this.outage = false;
    for (const cb of this.statusListeners) cb(s);
  }

  // --- docs ------------------------------------------------------------------------------------

  /** Open (or share) a doc. Resolves once local state is restored from IndexedDB. */
  async open(docId: string): Promise<DocHandle> {
    const existing = this.entries.get(docId);
    if (existing) {
      await existing.restored;
      return existing.handle;
    }
    const doc = new LoroDoc();
    const handle: DocHandle = { docId, doc, readOnly: false, settled: false, pendingCount: 0, epoch: 0 };
    let markRestored!: () => void;
    const entry: Entry = {
      handle,
      pendingIds: [],
      inFlight: [],
      listeners: new Set(),
      restored: new Promise<void>((r) => (markRestored = r)),
    };
    this.entries.set(docId, entry);

    const stored = await loadSnapshot(docId);
    if (stored.snapshot) doc.import(stored.snapshot);
    handle.epoch = stored.epoch;
    const pending = await loadPending(docId);
    for (const p of pending) {
      try {
        doc.import(p.payload);
      } catch {
        /* a pending update the snapshot already covers */
      }
    }
    entry.pendingIds = pending.map((p) => p.id);
    handle.pendingCount = pending.length;
    // Loro's UndoManager records only what happens after it exists: made here, before the first edit, so the
    // first Ctrl+Z (and "Dashboard deleted — Undo") has something to undo (bug hunt D4)
    undoManagerFor(doc);

    doc.subscribeLocalUpdates((bytes) => {
      if (handle.readOnly) return;
      // an implicit empty commit (around an import) also fires this; nothing to send then
      if (!hasOps(bytes)) return;
      void this.onLocalUpdate(entry, bytes);
    });
    doc.subscribe(() => this.notify(entry));

    markRestored();
    this.start();
    this.hello([docId]);
    return handle;
  }

  get(docId: string): DocHandle | undefined {
    return this.entries.get(docId)?.handle;
  }

  subscribeDoc(docId: string, cb: Listener): () => void {
    const e = this.entries.get(docId);
    if (!e) return () => {};
    e.listeners.add(cb);
    return () => e.listeners.delete(cb);
  }

  private notify(entry: Entry): void {
    for (const cb of entry.listeners) cb();
    this.scheduleSnapshot(entry);
  }

  private scheduleSnapshot(entry: Entry): void {
    const id = entry.handle.docId;
    if (this.snapshotTimers.has(id)) return;
    this.snapshotTimers.set(
      id,
      setTimeout(() => {
        this.snapshotTimers.delete(id);
        void saveSnapshot(id, entry.handle.doc.export({ mode: "snapshot" }), entry.handle.epoch);
      }, SNAPSHOT_EVERY_MS),
    );
  }

  private async onLocalUpdate(entry: Entry, bytes: Uint8Array): Promise<void> {
    // send first (latency), then mirror to IndexedDB so a reload can replay it
    const sent = this.send({ t: "update", docId: entry.handle.docId, payload: bytes });
    entry.handle.pendingCount += 1;
    const row = appendPending(entry.handle.docId, bytes);
    if (sent) entry.inFlight.push(row);
    this.refreshStatus();
    this.notify(entry);
    const id = await row;
    if (id !== null) entry.pendingIds.push(id);
  }

  /** "synced" only when the socket is open and every local write has been acknowledged. */
  private refreshStatus(): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    let pending = 0;
    let settled = true;
    for (const e of this.entries.values()) {
      pending += e.handle.pendingCount;
      if (!e.handle.settled) settled = false;
    }
    this.setStatus(pending > 0 || !settled ? "syncing" : "synced");
  }

  // --- socket -------------------------------------------------------------------------------------

  configure(url: string): void {
    this.urlOverride = url;
  }

  private url(): string {
    if (this.urlOverride) return this.urlOverride;
    const proto = location.protocol === "https:" ? "wss" : "ws";
    return `${proto}://${location.host}/ws`;
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.connect();
    window.addEventListener("online", () => this.reconnectSoon(0));
  }

  stop(): void {
    this.started = false;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.ws = null;
  }

  private connect(): void {
    if (!this.started || typeof WebSocket === "undefined") return;
    this.setStatus("connecting");
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url());
    } catch {
      this.reconnectSoon();
      return;
    }
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    ws.onopen = () => {
      this.backoffMs = 1000;
      this.setStatus("syncing");
      this.hello([...this.entries.keys()]);
      this.pingTimer = setInterval(() => this.send({ t: "ping" }), 25_000);
    };
    ws.onmessage = (ev) => this.onMessage(ev.data as ArrayBuffer);
    ws.onclose = (ev) => {
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.ws = null;
      if (ev.code === 4401 || ev.code === 4403) {
        this.setStatus("forbidden");
        this.settleAll();
        return; // a new session is needed; the app shows the login
      }
      this.setStatus("offline");
      this.settleAll();
      this.reconnectSoon();
    };
    ws.onerror = () => {
      /* onclose follows */
    };
  }

  private settleAll(): void {
    for (const e of this.entries.values()) {
      if (!e.handle.settled) {
        e.handle.settled = true;
        this.notify(e);
      }
    }
  }

  private reconnectSoon(delay = this.backoffMs): void {
    if (!this.started || this.ws || this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
    this.backoffMs = Math.min(30_000, this.backoffMs * 2);
  }

  private send(msg: Record<string, unknown>): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(encode(msg));
    return true;
  }

  private hello(docIds: string[]): void {
    if (!docIds.length || this.ws?.readyState !== WebSocket.OPEN) return;
    const docs: Record<string, Uint8Array> = {};
    const epochs: Record<string, number> = {};
    for (const id of docIds) {
      const e = this.entries.get(id);
      if (!e) continue;
      docs[id] = e.handle.doc.oplogVersion().encode();
      epochs[id] = e.handle.epoch;
    }
    this.send({ t: "hello", docs, epochs });
  }

  private onMessage(data: ArrayBuffer): void {
    let msg: Record<string, unknown>;
    try {
      msg = decode(new Uint8Array(data)) as Record<string, unknown>;
    } catch {
      return;
    }
    switch (msg.t) {
      case "ping":
        this.send({ t: "pong" });
        return;
      case "pong":
        return;
      case "schema": {
        const e = this.entries.get(String(msg.docId));
        if (!e) return;
        const version = Number(msg.version);
        if (typeof msg.epoch === "number") e.handle.epoch = msg.epoch;
        const ro = version > SCHEMA_VERSION;
        if (ro !== e.handle.readOnly) {
          e.handle.readOnly = ro;
          this.notify(e);
        }
        return;
      }
      case "sync": {
        const e = this.entries.get(String(msg.docId));
        if (!e) return;
        const payload = msg.payload as Uint8Array;
        e.handle.doc.import(payload);
        e.handle.settled = true;
        e.inFlight = []; // a (re)sync supersedes per-update acks: reconcilePending re-sends what is missing
        // everything the server now holds is covered; re-send anything it does not
        this.reconcilePending(e, msg.vv as Uint8Array);
        this.notify(e);
        return;
      }
      case "update": {
        const e = this.entries.get(String(msg.docId));
        if (!e) return;
        e.handle.doc.import(msg.payload as Uint8Array);
        return;
      }
      case "ack": {
        // the server persisted our oldest in-flight update: drop it from the offline queue
        const e = this.entries.get(String(msg.docId));
        if (!e) return;
        const row = e.inFlight.shift();
        e.handle.pendingCount = Math.max(0, e.handle.pendingCount - 1);
        if (row) {
          void row.then((id) => {
            if (id === null) return;
            e.pendingIds = e.pendingIds.filter((x) => x !== id);
            void clearPending([id]);
          });
        }
        this.refreshStatus();
        this.notify(e);
        return;
      }
      case "reset": {
        // the server replaced this doc (import `replace`): our replica must not merge back.
        // Drop it locally and start over from the server's state.
        const id = String(msg.docId);
        void wipeDoc(id).then(() => {
          if (typeof location !== "undefined" && typeof location.reload === "function") location.reload();
        });
        return;
      }
      case "error": {
        const code = String(msg.code);
        if (code === "sync.forbidden") {
          const e = this.entries.get(String(msg.docId));
          if (e) {
            e.handle.readOnly = true;
            e.handle.settled = true;
            this.notify(e);
          }
        }
        return;
      }
    }
  }

  /** After a `sync` frame the server's vv is known: everything it covers is acknowledged, and the
   *  ops it lacks (made offline, or racing the reconnect) are re-sent and kept as one pending row.
   *  The in-memory bookkeeping is synchronous so a local commit cannot interleave; IndexedDB trails. */
  private reconcilePending(entry: Entry, serverVvBytes: Uint8Array): void {
    const serverVv = VersionVector.decode(serverVvBytes);
    const exported = entry.handle.doc.export({ mode: "update", from: serverVv });
    const missing = hasOps(exported) ? exported : new Uint8Array();
    if (missing.length) this.send({ t: "update", docId: entry.handle.docId, payload: missing });
    const acked = entry.pendingIds.splice(0, entry.pendingIds.length);
    entry.handle.pendingCount = missing.length ? 1 : 0;
    void clearPending(acked);
    if (missing.length) {
      const row = appendPending(entry.handle.docId, missing);
      entry.inFlight.push(row);
      void row.then((id) => {
        if (id !== null) entry.pendingIds.push(id);
      });
    }
    this.refreshStatus();
    this.notify(entry);
  }
}

export const docManager = new DocManager();
